import { motion } from "framer-motion";
import { useState } from "react";
import {
  Fingerprint,
  Smartphone,
  Key,
  ShieldCheck,
  Eye,
  Lock,
  ChevronRight,
} from "lucide-react";
import ScreenHeader from "../components/ScreenHeader";

export const SecurityScreen = () => {
  const [biometric, setBiometric] = useState(true);
  const [twoFA, setTwoFA] = useState(true);
  const [loginAlerts, setLoginAlerts] = useState(true);
  const [txAlerts, setTxAlerts] = useState(true);

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -20, opacity: 0 }}
      transition={{ type: "spring", bounce: 0, duration: 0.3 }}
      className="h-full flex flex-col"
      data-testid="security-screen"
    >
      <ScreenHeader title="Security" testId="security-back-btn" />

      <div className="flex-1 overflow-y-auto phone-scroll px-6 pb-10">
        <h1 className="font-display text-[26px] font-bold tracking-tight">Security</h1>
        <p className="text-[13px] text-gray-500 mt-1.5 leading-snug">
          Authentication is handled by Finshark & your device. Shwiip never sees your
          banking credentials.
        </p>

        {/* Status card */}
        <div className="mt-5 rounded-3xl border border-emerald-100 bg-emerald-50/50 p-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-semibold text-[14px] text-emerald-900">
              Your account is protected
            </div>
          </div>
          <div className="text-[12px] text-emerald-800/80 mt-2 leading-snug">
            Biometric, 2FA and partner authentication are active.
          </div>
        </div>

        <Section label="Authentication" />
        <ToggleRow
          icon={Fingerprint}
          title="Biometric approval"
          subtitle="Use Face ID / Touch ID to confirm payments"
          value={biometric}
          onChange={setBiometric}
          testId="toggle-biometric"
        />
        <ToggleRow
          icon={Key}
          title="Two-factor authentication"
          subtitle="Required for high-value transfers"
          value={twoFA}
          onChange={setTwoFA}
          testId="toggle-2fa"
        />
        <LinkRow icon={Lock} title="Change PIN" testId="change-pin" />
        <LinkRow
          icon={Smartphone}
          title="Trusted devices"
          subtitle="2 devices active"
          testId="trusted-devices"
        />

        <Section label="Alerts" className="mt-4" />
        <ToggleRow
          icon={Eye}
          title="Login alerts"
          subtitle="Notify me when a new device signs in"
          value={loginAlerts}
          onChange={setLoginAlerts}
          testId="toggle-login-alerts"
        />
        <ToggleRow
          icon={ShieldCheck}
          title="Transaction alerts"
          subtitle="Notify me for every payment executed"
          value={txAlerts}
          onChange={setTxAlerts}
          testId="toggle-tx-alerts"
        />

        <div className="mt-5 rounded-2xl border border-gray-100 bg-gray-50 p-4 text-[11px] text-gray-500 leading-snug">
          Shwiip never sees your bank passwords. All sensitive authentication happens
          inside your banking app or device.
        </div>
      </div>
    </motion.div>
  );
};

const Section = ({ label, className = "" }) => (
  <div
    className={
      "text-[11px] uppercase tracking-widest font-bold text-gray-400 mt-5 mb-2 " +
      className
    }
  >
    {label}
  </div>
);

const ToggleRow = ({ icon: Icon, title, subtitle, value, onChange, testId }) => (
  <button
    data-testid={testId}
    onClick={() => onChange(!value)}
    className="w-full flex items-center gap-3 py-3 px-2 -mx-2 rounded-xl hover:bg-gray-50 transition-colors text-left"
  >
    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-gray-900" strokeWidth={2.2} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-[13px] text-gray-900">{title}</div>
      {subtitle && (
        <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">{subtitle}</div>
      )}
    </div>
    <span
      className={
        "relative w-10 h-6 rounded-full transition-colors flex-shrink-0 " +
        (value ? "bg-emerald-500" : "bg-gray-200")
      }
    >
      <span
        className={
          "absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform " +
          (value ? "translate-x-[18px]" : "translate-x-0.5")
        }
      />
    </span>
  </button>
);

const LinkRow = ({ icon: Icon, title, subtitle, testId }) => (
  <button
    data-testid={testId}
    className="w-full flex items-center gap-3 py-3 px-2 -mx-2 rounded-xl hover:bg-gray-50 transition-colors text-left"
  >
    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-gray-900" strokeWidth={2.2} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-[13px] text-gray-900">{title}</div>
      {subtitle && (
        <div className="text-[11px] text-gray-500 mt-0.5">{subtitle}</div>
      )}
    </div>
    <ChevronRight className="w-4 h-4 text-gray-400" strokeWidth={2.2} />
  </button>
);

export default SecurityScreen;
